import {
  ARROW_DIR,
  DELTA,
  type Dir,
  ICE_SIZE,
  type Level,
  type Monster,
  type Pos,
  posKey,
  type RunState,
  type SlideResult,
  type StepResult,
  samePos,
  type Tile,
} from './types'

const MAX_SLIDE_STEPS = 64

/** Anything outside the grid behaves like rock. */
export function tileAt(level: Level, pos: Pos): Tile {
  if (pos.r < 0 || pos.c < 0 || pos.r >= ICE_SIZE || pos.c >= ICE_SIZE) return '#'
  return (level.rows[pos.r]?.[pos.c] ?? '#') as Tile
}

function find(level: Level, tile: Tile): Pos | null {
  for (let r = 0; r < level.rows.length; r++) {
    const c = level.rows[r]?.indexOf(tile) ?? -1
    if (c >= 0) return { r, c }
  }
  return null
}

export function startOf(level: Level): Pos {
  const s = find(level, 'S')
  if (!s) throw new Error(`level ${level.id} has no start`)
  return s
}

export function doorOf(level: Level): Pos {
  const d = find(level, 'D')
  if (!d) throw new Error(`level ${level.id} has no door`)
  return d
}

export function hasKeyTile(level: Level): boolean {
  return level.rows.some((row) => row.includes('K'))
}

export function initialRun(level: Level, moves = 0): RunState {
  return { pos: startOf(level), moves, cracked: [], hasKey: false }
}

/** Where a yeti stands at time `t`: bounces between both ends of its path. */
export function monsterPos(m: Monster, t: number): Pos {
  const n = m.path.length
  if (n <= 1) return m.path[0] ?? { r: 0, c: 0 }
  const period = 2 * (n - 1)
  const k = ((t % period) + period) % period
  return m.path[k < n ? k : period - k]!
}

export function monsterPositions(level: Level, t: number): Pos[] {
  return (level.monsters ?? []).map((m) => monsterPos(m, t))
}

/** Flicker rocks are solid on even move counts. */
export function flickerSolid(t: number): boolean {
  return t % 2 === 0
}

/** Smallest cycle after which flicker rocks and every yeti are back where they were. */
export function levelPeriod(level: Level): number {
  let p = 1
  if (level.rows.some((row) => row.includes('='))) p = 2
  for (const m of level.monsters ?? []) {
    const mp = Math.max(1, 2 * (m.path.length - 1))
    p = lcm(p, mp)
  }
  return p
}

function lcm(a: number, b: number): number {
  const gcd = (x: number, y: number): number => (y === 0 ? x : gcd(y, x % y))
  return (a * b) / gcd(a, b)
}

/** Returns human-readable problems; an empty list means the level is well-formed. */
export function validateLevel(level: Level): string[] {
  const errors: string[] = []
  if (level.rows.length !== ICE_SIZE)
    errors.push(`expected ${ICE_SIZE} rows, got ${level.rows.length}`)
  level.rows.forEach((row, r) => {
    if (row.length !== ICE_SIZE) errors.push(`row ${r} has ${row.length} cells`)
    if (!/^[#.oSD^v<>=~K]+$/.test(row)) errors.push(`row ${r} has an unknown tile`)
  })
  const all = level.rows.join('')
  const count = (t: Tile) => all.split(t).length - 1
  if (count('S') !== 1) errors.push(`expected exactly one S, got ${count('S')}`)
  if (count('D') !== 1) errors.push(`expected exactly one D, got ${count('D')}`)
  if (count('K') > 1) errors.push('at most one key')
  const border = [
    level.rows[0] ?? '',
    level.rows[ICE_SIZE - 1] ?? '',
    ...level.rows.map((row) => (row[0] ?? '') + (row[ICE_SIZE - 1] ?? '')),
  ].join('')
  if (!/^#*$/.test(border)) errors.push('the outer ring must be rock')
  if (level.par < 1) errors.push('par must be at least 1')
  for (const [i, m] of (level.monsters ?? []).entries()) {
    if (m.path.length < 2) errors.push(`monster ${i} needs a path of at least 2 cells`)
    for (const p of m.path) {
      const t = tileAt(level, p)
      if (t !== '.' && t !== '~') errors.push(`monster ${i} path must stay on ice, found ${t}`)
    }
  }
  return errors
}

/**
 * One move. Slides from `run.pos` in `dir` until something stops the player; arrows redirect,
 * flicker rocks are solid only on even move counts, cracked ice gives way, yetis catch you (they
 * stand still while you slide, then take one step), the door is shut without the key.
 * Falling (hole, crack, yeti) restarts the attempt from the start tile; the move still counts.
 */
export function step(level: Level, run: RunState, dir: Dir): StepResult {
  const t = run.moves
  const monsters = level.monsters ?? []
  const monstersNow = monsters.map((m) => monsterPos(m, t))
  const monstersNext = monsters.map((m) => monsterPos(m, t + 1))
  const needsKey = hasKeyTile(level)
  const cracked = new Set(run.cracked)
  let hasKey = run.hasKey
  let gotKey = false
  const path: Pos[] = []
  let cur = run.pos
  let d = dir
  let looped = false

  const fail = (outcome: 'fell' | 'caught'): StepResult => ({
    run: { pos: startOf(level), moves: t + 1, cracked: [], hasKey: false },
    path,
    outcome,
    gotKey,
  })

  for (let steps = 0; ; steps++) {
    if (steps >= MAX_SLIDE_STEPS) {
      looped = true
      break
    }
    const next = { r: cur.r + DELTA[d].r, c: cur.c + DELTA[d].c }
    const tile = tileAt(level, next)
    if (tile === '#') break
    if (tile === '=' && flickerSolid(t)) break
    if (tile === 'D' && needsKey && !hasKey) break
    path.push(next)
    cur = next
    if (monstersNow.some((m) => samePos(m, next))) return fail('caught')
    if (tile === 'o') return fail('fell')
    if (tile === '~') {
      if (cracked.has(posKey(next))) return fail('fell')
      cracked.add(posKey(next))
    }
    if (tile === 'K' && !hasKey) {
      hasKey = true
      gotKey = true
    }
    if (tile === 'D') {
      return {
        run: { pos: next, moves: t + 1, cracked: [...cracked], hasKey },
        path,
        outcome: 'door',
        gotKey,
      }
    }
    const arrow = ARROW_DIR[tile]
    if (arrow) d = arrow
  }

  if (path.length === 0) {
    const blockedByDoor =
      tileAt(level, { r: cur.r + DELTA[d].r, c: cur.c + DELTA[d].c }) === 'D' && needsKey && !hasKey
    return { run, path, outcome: blockedByDoor ? 'locked' : 'stop', looped }
  }
  // Stopping on cracking ice breaks it under you.
  if (tileAt(level, cur) === '~') return fail('fell')
  // Yetis take their step after yours; standing in their way ends the attempt too.
  if (monstersNext.some((m) => samePos(m, cur))) return fail('caught')
  return {
    run: { pos: cur, moves: t + 1, cracked: [...cracked], hasKey },
    path,
    outcome: 'stop',
    looped,
    gotKey,
  }
}

/** Convenience for plain levels (no memory needed): slide from a position at move count 0. */
export function slide(level: Level, from: Pos, dir: Dir, moves = 0): SlideResult {
  const r = step(level, { pos: from, moves, cracked: [], hasKey: false }, dir)
  return { path: r.path, end: r.run.pos, outcome: r.outcome }
}

export function stars(moves: number, par: number): 1 | 2 | 3 {
  if (moves <= par) return 3
  if (moves <= par + 2) return 2
  return 1
}
